import React from 'react';
import { Box, Icons, useTheme } from '@ublend-npm/aulaui-core';
import { getSpaceWidgetColors } from './spaceWidgetColors';

type SpaceWidgetNavArrowsProps = {
  canScrollBack: boolean;
  canScrollForward: boolean;
  onScrollBack: () => void;
  onScrollForward: () => void;
};

/** Matches My Spaces `ScrollBox` recently-viewed arrow style. */
const getNavArrowSx = (enabled: boolean, color: string) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  color,
  cursor: enabled ? 'pointer' : 'not-allowed',
  opacity: enabled ? 1 : 0.3,
  transition: 'opacity 0.2s',
  outline: 'none',
  '&:hover': enabled ? { opacity: 0.7 } : {},
  '&:focus': { outline: 'none' },
  '&:focus-visible': enabled ? { opacity: 0.7 } : {},
});

const SpaceWidgetNavArrows = ({
  canScrollBack,
  canScrollForward,
  onScrollBack,
  onScrollForward,
}: SpaceWidgetNavArrowsProps) => {
  const { palette } = useTheme();
  const colors = getSpaceWidgetColors(palette);

  return (
    <Box
      alignItems="center"
      gap={1}
      flexShrink={0}
      sx={{
        // Figma: carousel arrows sit beside greeting from SM up; XS uses swipe only.
        display: { xs: 'none', sm: 'flex' },
      }}
    >
      <Box
        role="button"
        tabIndex={canScrollBack ? 0 : -1}
        aria-label="Scroll spaces left"
        aria-disabled={!canScrollBack}
        onClick={canScrollBack ? onScrollBack : undefined}
        onKeyDown={(event) => {
          if (!canScrollBack) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onScrollBack();
          }
        }}
        sx={getNavArrowSx(canScrollBack, colors.navArrowIcon)}
      >
        <Icons.ArrowBack />
      </Box>
      <Box
        role="button"
        tabIndex={canScrollForward ? 0 : -1}
        aria-label="Scroll spaces right"
        aria-disabled={!canScrollForward}
        onClick={canScrollForward ? onScrollForward : undefined}
        onKeyDown={(event) => {
          if (!canScrollForward) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onScrollForward();
          }
        }}
        sx={getNavArrowSx(canScrollForward, colors.navArrowIcon)}
      >
        <Icons.ArrowForward />
      </Box>
    </Box>
  );
};

export default SpaceWidgetNavArrows;
